import api from "./api";

function authHeaders() {
  const token = localStorage.getItem("tk446655440000");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const jsonHeaders = () => ({
  ...authHeaders(),
  "Content-Type": "application/json",
});

/**
 * Build JSON body for activate/deactivate/update.
 * API expects camelCase: teacherId, teacherIds, provinceId, …
 */
export function normalizeLicenseActionBody(body = {}) {
  const payload = {};

  const teacherId = body.teacherId ?? body.TeacherId;
  if (teacherId != null && teacherId !== "") {
    payload.teacherId = Number(teacherId);
  }

  const teacherIds = body.teacherIds ?? body.TeacherIds;
  if (Array.isArray(teacherIds) && teacherIds.length) {
    payload.teacherIds = teacherIds.map(Number).filter((n) => Number.isFinite(n));
  }

  const geoEntries = [
    ["provinceId", body.provinceId ?? body.ProvinceId],
    ["departmentId", body.departmentId ?? body.DepartmentId],
    ["subPrefectureId", body.subPrefectureId ?? body.SubPrefectureId],
    ["establishmentId", body.establishmentId ?? body.EstablishmentId],
  ];
  for (const [key, value] of geoEntries) {
    if (value != null && value !== "") payload[key] = Number(value);
  }

  if (!Object.keys(payload).length) {
    throw new Error("License action requires teacherId, teacherIds, or a geographic filter.");
  }

  return payload;
}

/** Body for POST /TeacherLicense/update (includes target license flag). */
export function normalizeLicenseUpdateBody(body = {}) {
  const payload = normalizeLicenseActionBody(body);
  const isLicensed = body.isLicensed ?? body.IsLicensed;
  if (isLicensed != null) payload.isLicensed = Boolean(isLicensed);
  return payload;
}

export function isLicenseApiFailure(res) {
  if (res == null) return false;
  const success = res.success ?? res.Success;
  return success === false;
}

export function getLicenseApiMessage(res, fallback = "") {
  return res?.message ?? res?.Message ?? fallback;
}

/** Pretty-print request/response for debug dialogs (temporary). */
export function formatLicenseApiDebug({
  endpoint = "",
  requestBody = null,
  status = null,
  responseBody = null,
  context = null,
} = {}) {
  const parts = [];
  if (endpoint) parts.push(`Endpoint: POST ${endpoint}`);
  if (status != null) parts.push(`HTTP status: ${status}`);
  if (context != null) {
    parts.push("UI context:");
    parts.push(JSON.stringify(context, null, 2));
  }
  parts.push("Request body:");
  parts.push(JSON.stringify(requestBody ?? {}, null, 2));
  parts.push("Response body:");
  parts.push(
    responseBody == null
      ? "null"
      : typeof responseBody === "string"
        ? responseBody
        : JSON.stringify(responseBody, null, 2)
  );
  return parts.join("\n");
}

/**
 * @returns {Promise<import('../layouts/teacher-license/types').TeacherLicenseSummary>}
 */
export async function fetchTeacherLicenseSummary() {
  const { data } = await api.get("/TeacherLicense/summary", { headers: authHeaders() });
  return data;
}

/**
 * @param {import('../layouts/teacher-license/types').TeacherLicenseListParams} params
 * @returns {Promise<import('../layouts/teacher-license/types').TeacherLicenseListResponse>}
 */
export async function fetchTeacherLicenseList(params) {
  const { data } = await api.get("/TeacherLicense/list", { params, headers: authHeaders() });
  return data;
}

/**
 * @param {import('../layouts/teacher-license/types').TeacherLicenseUpdateRequest} body
 * @returns {Promise<import('../layouts/teacher-license/types').TeacherLicenseUpdateResponse>}
 */
export async function updateTeacherLicense(body) {
  const { data } = await api.post("/TeacherLicense/update", normalizeLicenseUpdateBody(body), {
    headers: jsonHeaders(),
  });
  return data;
}

/**
 * @param {import('../layouts/teacher-license/types').TeacherLicenseUpdateRequest} body
 * @returns {Promise<import('../layouts/teacher-license/types').TeacherLicenseUpdateResponse>}
 */
export async function activateTeachers(body) {
  const { data } = await api.post("/TeacherLicense/activate", normalizeLicenseActionBody(body), {
    headers: jsonHeaders(),
  });
  return data;
}

/**
 * @param {import('../layouts/teacher-license/types').TeacherLicenseUpdateRequest} body
 * @returns {Promise<import('../layouts/teacher-license/types').TeacherLicenseUpdateResponse>}
 */
export async function deactivateTeachers(body) {
  const { data } = await api.post("/TeacherLicense/deactivate", normalizeLicenseActionBody(body), {
    headers: jsonHeaders(),
  });
  return data;
}
